import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import type { PassId } from "../models/PassId";
import { PassTypeSelector } from "./PassTypeSelector";
import { PassShapeSelector } from "./PassShapeSelector";
import { StandsMultiSelect } from "./StandsMultiSelect";

interface FormErrors {
	Name?: string;
	StandId?: string;
}

interface PassIdDetailViewerProps {
	passId: PassId | null;
	existingPassIds: PassId[];
	isSaving: boolean;
	onSave: (passId: PassId) => void;
	onClose: () => void;
}

export const PassIdDetailViewer = ({
	passId,
	existingPassIds,
	isSaving,
	onSave,
	onClose,
}: PassIdDetailViewerProps) => {
	const [formData, setFormData] = useState<PassId | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewPassId = !passId?.Id;

	// Validate form and return errors
	const validateForm = useMemo(() => {
		return (data: PassId | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				// Check for duplicate name (case-insensitive)
				const isDuplicate = existingPassIds.some(
					(pi) =>
						pi.Id !== data.Id &&
						pi.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			if (data?.StandId == null) {
				newErrors.StandId = "Stand is required";
			}

			return newErrors;
		};
	}, [existingPassIds]);

	// Check if form is valid
	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;

	// Check if new pass ID can be created
	const canCreatePassId = isNewPassId && isFormValid;

	useEffect(() => {
		if (passId) {
			setFormData(sanitizeNullStrings({ ...passId }));
		} else {
			setFormData(null);
		}
		// Reset validation state when passId changes
		setErrors({});
		setTouched({});
	}, [passId]);

	if (!passId) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a pass ID to view</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	// Handle field change (updates local state)
	const handleChange = (field: keyof PassId, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	// Mark field as touched and show error if any
	const handleBlur = (field: keyof PassId) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		// Update errors for touched field
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	// Auto-save for existing pass IDs (called on blur for text fields)
	const handleBlurSave = (field: keyof PassId, value: unknown) => {
		handleBlur(field);

		if (isNewPassId) return; // Don't auto-save new pass IDs

		// Check for validation errors on the field
		const currentErrors = validateForm(
			formData ? { ...formData, [field]: value } : null,
		);
		if (currentErrors[field as keyof FormErrors]) {
			return; // Don't save if there are validation errors
		}

		// Check if value actually changed
		const originalValue = passId[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			const updatedPassId = { ...formData, [field]: value };
			onSave(updatedPassId);
		}
	};

	// Auto-save for selects and checkboxes (immediate on change)
	const handleSelectChange = (field: keyof PassId, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		// Update errors after changing value
		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewPassId) return; // Don't auto-save new pass IDs

		// Check for validation errors on the field
		if (currentErrors[field as keyof FormErrors]) {
			return; // Don't save if there are validation errors
		}

		// Check if value actually changed
		if (passId[field] !== value) {
			const updatedPassId = { ...formData, [field]: value };
			onSave(updatedPassId);
		}
	};

	const standIds =
		formData.StandId == null
			? []
			: [
					formData.StandId,
					...(formData.ExtraStandIds ?? "")
						.split(",")
						.filter((id) => id !== "")
						.map(Number),
				];

	// The first stand is the main one; the rest are saved as extra stands.
	const handleStandsChange = (ids: number[]) => {
		const updated: PassId = {
			...formData,
			StandId: ids[0] ?? null,
			ExtraStandIds: ids.slice(1).join(","),
		};
		setFormData(updated);
		setTouched((prev) => ({ ...prev, StandId: true }));
		const currentErrors = validateForm(updated);
		setErrors(currentErrors);

		if (isNewPassId || currentErrors.StandId) return;
		if (
			updated.StandId !== passId.StandId ||
			updated.ExtraStandIds !== (passId.ExtraStandIds ?? "")
		) {
			onSave(updated);
		}
	};

	// Create new pass ID (explicit save)
	const handleCreate = () => {
		// Mark all required fields as touched to show errors
		setTouched({ Name: true, StandId: true });
		setErrors(formErrors);

		if (formData && canCreatePassId) {
			onSave(formData);
		}
	};

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={isNewPassId ? "New Pass ID" : formData.Name}
				active={isNewPassId ? undefined : passId.Active}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					isNewPassId ? (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreatePassId || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					) : undefined
				}
			/>
			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<DetailSection title="Basic Information" icon={<InfoOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Name"
									value={formData.Name}
									onChange={(e) => handleChange("Name", e.target.value)}
									onBlur={(e) => handleBlurSave("Name", e.target.value)}
									fullWidth
									size="small"
									required
									error={touched.Name && !!errors.Name}
									helperText={touched.Name && errors.Name}
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 6 }} sx={EDITABLE_FIELD_SX}>
								<StandsMultiSelect
									value={standIds}
									onChange={handleStandsChange}
									required
									error={touched.StandId && !!errors.StandId}
									helperText={touched.StandId ? errors.StandId : undefined}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								<TextField
									label="Description"
									value={formData.Description ?? ""}
									onChange={(e) => handleChange("Description", e.target.value)}
									onBlur={(e) => handleBlurSave("Description", e.target.value)}
									fullWidth
									size="small"
									multiline
									rows={2}
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>

							<Grid2 size={{ xs: 6, md: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.Active}
											onChange={(e) =>
												handleSelectChange("Active", e.target.checked)
											}
											size="small"
										/>
									}
									label="Active"
									sx={{ ml: 0 }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Configuration" icon={<TuneOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<PassTypeSelector
									value={formData.PassTypeId}
									onChange={(value) => handleSelectChange("PassTypeId", value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 6 }}>
								<PassShapeSelector
									value={formData.PassShapeId}
									onChange={(value) => handleSelectChange("PassShapeId", value)}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Scheduling" icon={<ScheduleOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Scheduled Tonnage"
									type="number"
									value={formData.ScheduledTonnage}
									onChange={(e) =>
										handleChange("ScheduledTonnage", Number(e.target.value))
									}
									onBlur={(e) =>
										handleBlurSave("ScheduledTonnage", Number(e.target.value))
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{
										inputLabel: { shrink: true },
										htmlInput: { step: 0.1, min: 0 },
									}}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Scheduled Billets"
									type="number"
									value={formData.ScheduledBillets ?? 0}
									onChange={(e) =>
										handleChange("ScheduledBillets", Number(e.target.value))
									}
									onBlur={(e) =>
										handleBlurSave("ScheduledBillets", Number(e.target.value))
									}
									fullWidth
									size="small"
									sx={EDITABLE_FIELD_SX}
									slotProps={{
										inputLabel: { shrink: true },
										htmlInput: { step: 1, min: 0 },
									}}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>
				</Box>
			</DetailBody>
		</Box>
	);
};
